import apiClient from './client';
import {
  VenueTastingEvent,
  EventGuest,
  EventStatusResponse,
  InvitationResponse,
  TastingSession,
  PreparedTasting,
  ApiResponse,
} from '@/types';

// =====================================================
// Event Public API
// =====================================================

export const eventApi = {
  // Get event by access code
  getByAccessCode: async (accessCode: string): Promise<VenueTastingEvent> => {
    const response = await apiClient.get<ApiResponse<VenueTastingEvent>>(`/public/events/${accessCode}`);
    return response.data.data!;
  },

  // Join event by access code
  joinByCode: async (accessCode: string, guestName: string, email?: string): Promise<EventGuest> => {
    const response = await apiClient.post<ApiResponse<EventGuest>>(`/public/events/${accessCode}/join`, {
      guest_name: guestName,
      email,
    });
    return response.data.data!;
  },

  // Get event status (for polling)
  getStatus: async (accessCode: string): Promise<EventStatusResponse> => {
    const response = await apiClient.get<ApiResponse<EventStatusResponse>>(`/public/events/${accessCode}/status`);
    return response.data.data!;
  },
};

// =====================================================
// Invitation Public API
// =====================================================

export const invitationApi = {
  // Get invitation details
  get: async (token: string): Promise<InvitationResponse> => {
    const response = await apiClient.get<ApiResponse<InvitationResponse>>(`/public/invitations/${token}`);
    return response.data.data!;
  },

  // Accept invitation
  accept: async (token: string, guestName: string): Promise<EventGuest> => {
    const response = await apiClient.post<ApiResponse<EventGuest>>(`/public/invitations/${token}/accept`, {
      guest_name: guestName,
    });
    return response.data.data!;
  },

  // Decline invitation
  decline: async (token: string): Promise<void> => {
    await apiClient.post(`/public/invitations/${token}/decline`);
  },
};

// =====================================================
// Tasting Session Public API
// =====================================================

export interface StartSessionRequest {
  venue_id: number;
  tasting_type?: string;
  guest_name?: string;
  event_id?: number;
  guest_id?: number;
}

export interface AddSpiritRequest {
  spirit_id?: number;
  menu_item_id?: number;
  spirit_name: string;
  spirit_brand?: string;
  known_botanicals?: string[];
}

export interface SubmitStepRequest {
  step: string;
  spirit_index: number;
  rating?: number;
  notes?: string;
  descriptors?: string[];
  detected_botanicals?: string[];
}

export const tastingApi = {
  // Start a new tasting session
  startSession: async (data: StartSessionRequest): Promise<TastingSession> => {
    const response = await apiClient.post<ApiResponse<TastingSession>>('/public/tasting/start', data);
    return response.data.data!;
  },

  // Get session by UUID
  getSession: async (uuid: string): Promise<TastingSession> => {
    const response = await apiClient.get<ApiResponse<TastingSession>>(`/public/tasting/${uuid}`);
    return response.data.data!;
  },

  // Add spirit to session
  addSpirit: async (sessionUuid: string, data: AddSpiritRequest) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/spirit`, data);
    return response.data.data;
  },

  // Submit step response
  submitStep: async (sessionUuid: string, data: SubmitStepRequest) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/step`, data);
    return response.data.data;
  },

  // Complete session
  completeSession: async (sessionUuid: string) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/complete`);
    return response.data.data;
  },

  // Abandon session
  abandonSession: async (sessionUuid: string) => {
    await apiClient.post(`/public/tasting/${sessionUuid}/abandon`);
  },

  // AI Guidance
  getGuidance: async (sessionUuid: string, step: string, spiritIndex: number) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/ai/guide`, {
      step,
      spirit_index: spiritIndex,
    });
    return response.data.data;
  },

  // AI Botanical Detection Feedback
  detectBotanicals: async (sessionUuid: string, spiritIndex: number, detectedBotanicals: string[]) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/ai/botanicals`, {
      spirit_index: spiritIndex,
      detected_botanicals: detectedBotanicals,
    });
    return response.data.data;
  },

  // Generate AI Tasting Notes
  generateNotes: async (sessionUuid: string) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/ai/notes`);
    return response.data.data;
  },

  // Get AI Recommendations
  getRecommendations: async (sessionUuid: string) => {
    const response = await apiClient.post(`/public/tasting/${sessionUuid}/ai/recommend`);
    return response.data.data;
  },
};

// =====================================================
// Prepared Tasting Public API
// =====================================================

export const preparedTastingApi = {
  // Get prepared tasting by UUID
  get: async (uuid: string): Promise<PreparedTasting> => {
    const response = await apiClient.get<ApiResponse<PreparedTasting>>(`/public/tasting/prepared/${uuid}`);
    return response.data.data!;
  },

  // Start a session from prepared tasting
  start: async (uuid: string, guestEmail?: string, marketingConsent?: boolean): Promise<TastingSession> => {
    const body: { guest_email?: string; marketing_consent?: boolean } = {};
    if (guestEmail) {
      body.guest_email = guestEmail;
      body.marketing_consent = marketingConsent || false;
    }
    const response = await apiClient.post<ApiResponse<TastingSession>>(
      `/public/tasting/start-from-prepared/${uuid}`,
      body
    );
    return response.data.data!;
  },
};

export default {
  event: eventApi,
  invitation: invitationApi,
  tasting: tastingApi,
  preparedTasting: preparedTastingApi,
};
